import {Component} from '@angular/core';
import {Router} from '@angular/router';
import {ClientManagementService} from '@services/client-management.service';
import {NotificationService} from '@notifications';
import {LocalStorageService} from '@services/local-storage.service';
import {SidebarService} from '@services/sidebar.service';
import {SelectedClientModel} from '@models/selected-client.model';
import {UserGuideService} from '@services/user-guide.service';
import {IClient, INTEGRATION_LABELS, TIntegration} from '@shared/interface';
import {finalize} from 'rxjs/operators';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {BsDropdownModule} from 'ngx-bootstrap/dropdown';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {ButtonComponent} from '../../../controls/button/button.component';
import {SearchInputComponent} from '../../../shared/reusable-components/search-input/search-input.component';
import {UserGuideDirective} from '../../../user-guide/user-guide.directive';
import {SearchNamePipe} from '../../../common/pipes/search-name.pipe';
import {USER_GUIDE_I18N} from '../../../user-guide/guide-i18n.constants';

type TIntegrationFilter = TIntegration | '' | 'no_integration';
type TIntegrationFilterItem = Record<TIntegrationFilter, string>;

@Component({
  selector: 'app-sidebar-component',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    BsDropdownModule,
    MatProgressSpinnerModule,
    ButtonComponent,
    SearchInputComponent,
    UserGuideDirective,
    SearchNamePipe
  ]
})
export class SidebarComponent {
  readonly guideKeys = USER_GUIDE_I18N;
  sidebarCollapsed = false;
  loading: boolean = true;
  originalClients: IClient[] = [];
  clients: IClient[] = [];
  selectedClient: SelectedClientModel;
  searchName: string = '';

  collapsedFirms: number[] = [];

  selectedIntegration: TIntegrationFilter = '';
  integrationLabels: TIntegrationFilterItem = {
    '': 'All Integrations',
    ...INTEGRATION_LABELS,
    'no_integration': 'No Integration',
  };
  integrationFilterItems = Object.keys(this.integrationLabels)
  .map(key => ({
    key: key as TIntegrationFilter,
    label: this.integrationLabels[key],
  }));

  constructor(
    private clientManagementService: ClientManagementService,
    private sidebarService: SidebarService,
    private router: Router,
    private localStorageService: LocalStorageService,
    private notification: NotificationService,
    private userGuideService: UserGuideService
  ) {
    this.collapsedFirms = localStorageService.getCollapsedFirms();
    this.selectedClient = this.localStorageService.getSelectedClient();
    this.sidebarService.clientListForceReload.subscribe((username) => {
      // if reload is forced with username, it is not supposed to be cached
      this.clientManagementService.clientsCache$ = null;
      this.load(username);
    });
    this.sidebarService.pickClientEmitter.subscribe(({username, skipNotify}) => {
      const value = username ?
        this.clients?.find((item) => item.username === username) : null;
      if (username) {
        // will trigger select only if value presented in current list (might be filtered by integration)
        if (value) {
          this.onSelectClient(value, skipNotify);
        }
      } else {
        this.onSelectClient(this.originalClients?.[0], skipNotify);
      }
    });
    this.sidebarService.pickClientByIdEmitter.subscribe(({clientId, skipNotify}) => {
      // resolve the client by numeric id from the loaded list, then select it
      const value = this.originalClients?.find((item) => item.id === clientId);
      if (value) {
        this.onSelectClient(value, skipNotify);
      }
    });

    this.nextGuideStep = this.nextGuideStep.bind(this);
    this.load();
  }

  load(usernameToPickAfterLoad?: string) {
    this.loading = true;
    this.clientManagementService.get(this.selectedIntegration as TIntegration)
    .pipe(finalize(() => this.loading = false))
    .subscribe({
      next: (clients) => {
        if (clients) {
          this.originalClients = clients;
          this.clients = this.groupItems(clients);
          if (usernameToPickAfterLoad) {
            const clientToPick =
              this.clients.find((client) => client.username === usernameToPickAfterLoad);
            if (clientToPick) {
              this.onSelectClient(clientToPick);
            }
          }
        }
      },
      error: (error) => this.notification.error(error || 'Failed to get client list'),
    });
  }

  groupItems(clients: IClient[]) {
    const result: IClient[] = [];
    const clientsByGroups = {};
    const restClients = [];
    for (const client of clients) {
      if (client.clientGroupId) {
        client.isClientWithFirm = true;
        clientsByGroups[client.clientGroupId] ?
          clientsByGroups[client.clientGroupId].push(client) :
          clientsByGroups[client.clientGroupId] = [client];
      } else {
        restClients.push(client);
      }
    }
    for (const group of Object.keys(clientsByGroups)) {
      const id = parseInt(group, 10);
      const clientData: IClient = clientsByGroups[group][0];
      result.push(
        {
          id,
          name: clientData.clientGroupName,
          clientGroupId: null,
          clientGroupName: clientData.clientGroupName,
          isFirm: true,
          // column to filter clients by search input, but save firm record in list
          seek: clientsByGroups[group].map((client) => client.name).join(''),
          // default collapsed state
          collapsed: this.collapsedFirms.includes(id),
        }
      );
      result.push(...clientsByGroups[group]);
    }

    result.push(...restClients);

    return result;
  }

  // groupItems(clients: IClient[]) {
  //   const result: IClient[] = [];
  //   let currentFirmId;
  //   for (const client of clients) {
  //     if (client.clientGroupId) {
  //       if (currentFirmId?.id !== client.clientGroupId) {
  //         currentFirmId = {
  //           id: client.clientGroupId,
  //           name: client.clientGroupName,
  //           clientGroupId: null,
  //           clientGroupName: client.clientGroupName,
  //           isFirm: true,
  //           // column to filter clients by search input, but save firm record in list
  //           seek: client.name,
  //           // default collapsed state
  //           collapsed: this.collapsedFirms.includes(client.clientGroupId),
  //         };
  //         // default collapsed state
  //         // this.collapsedFirms.push(currentFirmId.id);
  //         result.push(currentFirmId);
  //       } else {
  //         currentFirmId.seek += client.name;
  //       }
  //       result.push({...client, isClientWithFirm: true});
  //     } else {
  //       result.push(client);
  //     }
  //   }
  //
  //   return result;
  // }

  /**
   * select client
   * @param clientData - selected client data
   * @param skipNotify - if need to notify
   */
  onSelectClient(clientData, skipNotify?) {
    if (!clientData) {
      return;
    }
    const fullName = clientData.name ? clientData.name : '';
    const role = clientData.role ? clientData.role : '';
    const accountsSize = clientData.accountsSize ? clientData.accountsSize : '';
    const phone = clientData.phone ? clientData.phone : '';
    const allowSms = clientData.allowSms ? clientData.allowSms : '';
    this.selectedClient = {id: clientData.id, username: clientData.username, fullName, role, accountsSize, phone, allowSms};
    this.localStorageService.setSelectedClient(this.selectedClient);
    if (this.router.url === '/') {
      this.router.navigate(['summary']);
    } else if (!skipNotify) {
      this.sidebarService.emitSelectedClient(this.selectedClient, true);
    }
  }

  nextGuideStep() {
    // group headers sit in the same list but have no username
    this.onSelectClient(this.clients.find((client) => client.username));
    this.router.navigate(['/banks/connections']).then(() => {
      setTimeout(() => this.userGuideService.initStep(1), 500);
    });
  }

  addClient() {
    this.router.navigate(['crm', 'client-list', {add: true}], {skipLocationChange: true});
  }

  toggle(client: IClient) {
    if (this.searchName) {
      return;
    }
    const isCollapsed = !!this.collapsedFirms.find((item) => item === client.id);
    if (isCollapsed) {
      this.collapsedFirms = this.collapsedFirms.filter((item) => item !== client.id);
    } else {
      this.collapsedFirms = [...this.collapsedFirms, client.id];
    }
    client.collapsed = !client.collapsed;
    this.localStorageService.saveCollapsedFirms(this.collapsedFirms);
  }
}
